import { Typography } from 'antd';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Sticky } from 'react-sticky';
import styled, { css } from 'styled-components';
import { Breadcrumb } from 'components/atoms/breadcrumb';
import { Text02 } from 'components/styles';
import { queueMacrotask } from 'utils/macrotask';
import { useHeaderHeight } from '../use-header-height';
import { usePageScrollTop } from '../use-page-scroll-top';

const { Paragraph } = Typography;

const TitleComponent = styled.h4.attrs({ 'data-cy': 'page.header.title' })`
  margin: 0;
  height: var(--height-lg);
`;

const Icon = styled.span.attrs({ 'data-cy': 'page.header.title.icon' })``;

const Text = styled.span.attrs({ 'data-cy': 'page.header.title.text' })``;

const SubText = styled(Paragraph).attrs({
  'data-cy': 'page.header.title.subtext',
})``;

type Title = typeof TitleComponent & {
  Icon: typeof Icon;
  Text: typeof Text;
};

const Title: Title = TitleComponent as any;

Title.Icon = Icon;
Title.Text = Text;

const Tabs = styled.div``;

export const Captions = styled.div``;

interface HeaderProps {
  title: ReactNode;
  /** @deprecated */
  compact?: boolean;
  tabs?: ReactNode;
  sticky?: boolean;
  breadcrumbs?: ReactNode;
  subText?: ReactNode;
  isBigIcon?: boolean;
}

const HeaderWrapper = styled.div<{ compact?: boolean; isBigIcon?: boolean }>`
  margin: 0;
  padding: 0;

  ${Captions} {
    display: flex;
    padding-bottom: var(--spacing-01);
    transition: padding var(--animation-duration-medium) ease;
  }

  ${Breadcrumb} {
    + ${Captions} {
      padding-top: 0;
    }
  }

  ${Title} {
    display: inline-flex;
    align-items: center;
    font-size: var(--heading-5-size);
    transition: transform var(--animation-duration-medium) ease;
    transform: scale(${({ compact }) => (compact ? 1 : 1)});
    transform-origin: 0;
  }

  ${Title} > ${Icon} {
    margin-right: ${({ isBigIcon }) =>
      isBigIcon ? 'var(--layout-01)' : 'var(--spacing-03)'};
    display: flex;
  }

  ${SubText} {
    ${Text02};

    color: var(--gray-80);
    /* padding-left: var(--layout-01); */
    letter-spacing: 0.0032em;
    margin-bottom: var(--spacing-05);
  }
`;

const Spacer = styled.div`
  padding-bottom: 1px;
  transform: scaleY(0);
  transition: transform var(--animation-duration-medium) ease;
  transform-origin: 0 0;
`;

const HeaderComponent: FC<HeaderProps> = ({
  sticky,
  title,
  tabs,
  breadcrumbs,
  subText,
  ...props
}) => {
  const [scrolled, setScrolled] = useState(false);
  const top = usePageScrollTop();
  const isCompact = true;
  const height = useHeaderHeight();

  useEffect(() => {
    if (!scrolled && isCompact) {
      queueMacrotask(() => setScrolled(true));
    }
  }, [scrolled, setScrolled, isCompact]);

  return sticky ? (
    <div>
      {scrolled && top === 0 && (
        <Spacer style={{ transform: `scaleY(${height})` }} />
      )}
      <Sticky relative disableCompensation={top === 0}>
        {({ style }) => (
          <HeaderWrapper
            id="page_header"
            compact={isCompact}
            {...props}
            style={style}
          >
            {breadcrumbs && <Breadcrumb>{breadcrumbs}</Breadcrumb>}
            <Captions>{title}</Captions>
            {subText && <SubText>{subText}</SubText>}
            {tabs}
          </HeaderWrapper>
        )}
      </Sticky>
    </div>
  ) : (
    <HeaderWrapper id="page_header" data-cy="page_header" compact {...props}>
      {breadcrumbs && <Breadcrumb>{breadcrumbs}</Breadcrumb>}
      <Captions>{title}</Captions>
      {subText && <SubText>{subText}</SubText>}
      {tabs}
    </HeaderWrapper>
  );
};

interface Props {
  $white?: boolean;
}
const StyledHeader = styled(HeaderComponent)<Props>`
  ${({ $white }) => css`
    background: ${$white ? 'var(--white-100)' : 'var(--coolest-gray-10)'};
  `};
`;

type Header = typeof StyledHeader & {
  Title: typeof Title;
  Tabs: typeof Tabs;
};

const Header: Header = StyledHeader as any;

Header.Title = Title;
Header.Tabs = Tabs;

export { Header };
